import React, { useState } from "react";
import Breadcrumb from "../../../Components/Common/Breadcrumb";
import { faAdd } from "@fortawesome/free-solid-svg-icons";
import Table from "../../../Components/Common/Table";
import useSWR from "swr";
import { topicUrls } from "../../../Api/Auth/ApiUrls";
import QueryString from "qs";
import useTable from "../../../hooks/useTable";
import TableHead from "../../../Components/Common/TableHead";
import { Loader } from "../../../Components/Common/Loader";
import NoData from "../../../Components/Common/NoData";
import Pagination from "../../../Components/Common/Pagination/Pagination";
import { useLocation } from "react-router-dom";
import Modal from "../../../Components/Common/Modal";
import useToastError from "../../../hooks/useErrorToast";
import useSWRMutation from "swr/mutation";
import {
  sendPostRequestAuth,
  sendPutRequestAuth,
} from "../../../Api/swrConfig";
import { toast } from "react-toastify";
import Tabs from "../../../Components/Common/Tabs";
import { practiceHubTabsData } from "../../../data/SidebarData";
import TopicRow from "./Components/TopicRow";
import TopicForm from "./Components/TopicForm";

const tableColumnData = [
  { id: "title", label: "Title" },
  { id: "description", label: "Description" },
  { id: "subtopic", label: "Sub Topic" },
  { id: "category", label: "Category" },
  { id: "createdAt", label: "Created" },
];

const TopicAdmin = () => {
  const [row, setRow] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: null,
    subtopic: null,
  });
  const [errors, setErrors] = useState({});
  const { showErrorToast } = useToastError();
  const [isModalOpen, setModalOpen] = useState(false);
  const location = useLocation();

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    useTable({ defaultOrderBy: "" });

  const apiQueryParams = {
    offset: (page - 1) * rowsPerPage,
    limit: rowsPerPage,
  };
  const apiQueryString = QueryString.stringify(apiQueryParams);

  const { isLoading, data, mutate } = useSWR(topicUrls.list(apiQueryString));

  const handleInputChange = (name, value) => {
    setFormData((prevState) => ({
      ...prevState,
      [name]: value, // Set only the field that's changed
    }));
  };

  const onEditClick = (row) => {
    setRow(row);
    handleInputChange("title", row.title);
    handleInputChange("description", row.description);
    handleInputChange("category", row.category?._id);
    handleInputChange(
      "subtopic",
      row.subtopic?.map((item) => ({ label: item.title, value: item._id }))
    );
    setModalOpen(true);
  };

  const { trigger, isMutating } = useSWRMutation(
    topicUrls.create,
    sendPostRequestAuth,
    {
      onSuccess: (data) => {
        toast.success("Success");
        mutate();
        setModalOpen(false);
      },
      onError: (error) => {
        showErrorToast(error);
      },
    }
  );
  const { trigger: EditTrigger, isMutating: editMutating } = useSWRMutation(
    topicUrls.edit(row?._id),
    sendPutRequestAuth,
    {
      onSuccess: (data) => {
        toast.success("Success");
        mutate();
        setModalOpen(false);
      },
      onError: (error) => {
        showErrorToast(error);
      },
    }
  );

  const actionButtons = [
    {
      label: "Create",
      icon: faAdd,
      onClick: () => {
        setRow(null);
        setFormData({
          title: "",
          description: "",
          category: null,
          subtopic: null,
        });
        setModalOpen(true);
      },
    },
  ];

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title) newErrors.title = "Title is required";
    if (!formData.subtopic) newErrors.subtopic = "Sub topic is required";
    if (!formData.category) newErrors.category = "Category is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    const modifiedData = {
      ...formData,
      category:
        typeof formData.category === "object"
          ? formData.category.value
          : formData.category,
      subtopic: formData.subtopic?.map((item) => item.value),
    };
    row ? EditTrigger(modifiedData) : trigger(modifiedData);
  };

  return (
    <div>
      <Breadcrumb actionButtons={actionButtons} />
      <Tabs tabs={practiceHubTabsData} />

      <Table>
        <TableHead rowCount={data?.data.length} headerData={tableColumnData} />
        <tbody className={`divide-y divide-gray-200`}>
          {!isLoading ? (
            <>
              {data?.data.map((row) => (
                <TopicRow
                  key={row._id}
                  row={row}
                  mutate={mutate}
                  onEditClick={onEditClick}
                />
              ))}
            </>
          ) : (
            <tr>
              <td colSpan="100" className="text-center py-4">
                <Loader />
              </td>
            </tr>
          )}
        </tbody>
        <NoData isNotFound={!data?.data.length && !isLoading} />
      </Table>
      <Pagination
        totalRows={data?.pagination.totalItems || 0}
        currentPage={page}
        rowsPerPage={rowsPerPage}
        currentPath={location.pathname}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <Modal
        isOpen={isModalOpen}
        title={row ? "Edit Topic" : "Create Topic"}
        onConfirm={handleSubmit}
        onCancel={() => {
          setErrors({});
          setModalOpen(false);
        }}
        isLoading={isMutating || editMutating}
        formWidth
      >
        <TopicForm
          errors={errors}
          handleSubmit={handleSubmit}
          onChange={handleInputChange}
          formData={formData}
        />
      </Modal>
    </div>
  );
};

export default TopicAdmin;
